/**
 * Typed API client.
 *
 * Single chokepoint for HTTP traffic in the dashboard. Every resource file
 * (`lib/api/strategies.ts`, `lib/api/brokers.ts`, …) builds on top.
 *
 * Responsibilities (institutional defaults):
 *   • Bearer token from `lib/auth/session` (api_key today, JWT in Tier B6 —
 *     wire shape unchanged).
 *   • Idempotency-Key generated on every mutating request.
 *   • Exponential-backoff retry on network / 5xx / timeout (NOT on 4xx).
 *   • Native fetch + AbortSignal pass-through.
 *   • Per-request timeout (default 15s) — composable with caller AbortSignal.
 *   • Mock side-channel unwrap: a `{ __mock: true, ... }` envelope from MSW
 *     becomes `meta.mock = true` on the response (so the UI badges the data).
 *   • 401 → `signOut('401')` on the session store; throws `ApiAuthError`.
 *   • 422 → `ApiValidationError` with the array of Pydantic issues preserved.
 *   • Zod parser at the boundary — every resource hook passes a schema so
 *     malformed responses are surfaced as `ApiError` rather than crashing
 *     React.
 *   • `request_id` propagation: the client mints one on every call and
 *     sends it as `X-Request-Id` so backend logs and frontend audit events
 *     can be cross-referenced.
 *
 * Designed to be safe under SSR (no Window references; storage access goes
 * through the session store which has an SSR-safe fallback).
 */

import type { z } from 'zod';

import { authHeader, useSessionStore } from '@/lib/auth/session';

import {
  ApiAbortedError,
  ApiAuthError,
  ApiConflictError,
  ApiError,
  ApiForbiddenError,
  ApiNetworkError,
  ApiNotFoundError,
  ApiRateLimitError,
  ApiServerError,
  ApiTimeoutError,
  ApiValidationError,
  isRetryableError,
  type ApiErrorMeta,
} from './errors';
import { DEFAULT_RETRY, fullJitter, wait, type RetryConfig } from './_retry';
import { newIdempotencyKey, requiresIdempotencyKey } from './_idempotency';

/* ============================================================================
 * Configuration
 * ============================================================================ */

export interface ApiClientConfig {
  baseUrl: string;
  /** Per-request timeout in ms. Default 15s. */
  timeoutMs: number;
  /** Retry policy. Defaults to {@link DEFAULT_RETRY}. */
  retry: RetryConfig;
  /** Hook fired on every 401; defaults to `signOut('401')`. */
  onAuthError: () => void;
}

const defaultBaseUrl = (): string => {
  const env = process.env.NEXT_PUBLIC_API_BASE_URL;
  if (env !== undefined && env !== '') {
    return env;
  }
  return 'http://localhost:8000';
};

let config: ApiClientConfig = {
  baseUrl: defaultBaseUrl(),
  timeoutMs: 15_000,
  retry: DEFAULT_RETRY,
  onAuthError: () => {
    try {
      useSessionStore.getState().signOut('401');
    } catch {
      /* SSR / no-window — ignore */
    }
  },
};

export const configureApiClient = (next: Partial<ApiClientConfig>): void => {
  config = { ...config, ...next };
};

export const getApiClientConfig = (): Readonly<ApiClientConfig> => config;

/* ============================================================================
 * Request shape
 * ============================================================================ */

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions<TSchema extends z.ZodTypeAny = z.ZodTypeAny> {
  method?: HttpMethod | undefined;
  /** Query params; values are URL-encoded; arrays expand to `?k=a&k=b`. */
  query?:
    | Record<string, string | number | boolean | null | undefined | Array<string | number>>
    | undefined;
  /** JSON body (auto-serialised). Use `formData` for multipart. */
  body?: unknown;
  /** Pre-built FormData — bypasses JSON serialisation. */
  formData?: FormData | undefined;
  /** Zod schema used to validate the parsed response. */
  schema?: TSchema | undefined;
  /** Override the idempotency key (use the original key when retrying a known-failed POST). */
  idempotencyKey?: string | undefined;
  /** Caller-supplied abort signal. Composed with the per-request timeout. */
  signal?: AbortSignal | undefined;
  /** Per-call retry override. */
  retry?: Partial<RetryConfig> | undefined;
  /** Per-call timeout override (ms). */
  timeoutMs?: number | undefined;
  /** Extra headers. Bearer + Idempotency + X-Request-Id are added automatically. */
  headers?: Record<string, string> | undefined;
}

export interface ApiResult<T> {
  data: T;
  meta: {
    mock: boolean;
    request_id: string;
    attempts: number;
    status: number;
    /** Lower-cased response headers. Useful for `x-total-count` on paged
     * endpoints. Populated only on successful responses; never on errors. */
    headers?: Record<string, string>;
  };
}

/* ============================================================================
 * Utility: query string assembly
 * ============================================================================ */

const buildSearch = (query: RequestOptions['query']): string => {
  if (query === undefined) {
    return '';
  }
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) {
      continue;
    }
    if (Array.isArray(v)) {
      for (const item of v) {
        sp.append(k, String(item));
      }
    } else {
      sp.append(k, String(v));
    }
  }
  const s = sp.toString();
  return s === '' ? '' : `?${s}`;
};

const joinUrl = (base: string, path: string): string => {
  const trimmedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
};

/* ============================================================================
 * Body parsing
 * ============================================================================ */

interface ParsedResponse {
  body: unknown;
  mock: boolean;
}

const parseResponse = async (resp: Response): Promise<ParsedResponse> => {
  const text = await resp.text();
  if (text === '') {
    return { body: null, mock: false };
  }
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    body = text;
  }
  // Unwrap the MSW mock side-channel envelope.
  if (
    body !== null &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    (body as { __mock?: unknown }).__mock === true &&
    'data' in body
  ) {
    return { body: (body as { data: unknown }).data, mock: true };
  }
  return { body, mock: false };
};

/* ============================================================================
 * Status → typed error mapping
 * ============================================================================ */

const throwForStatus = (resp: Response, body: unknown, meta: ApiErrorMeta): never => {
  switch (resp.status) {
    case 401:
      throw new ApiAuthError(meta, body);
    case 403:
      throw new ApiForbiddenError(meta, body);
    case 404:
      throw new ApiNotFoundError(meta, body);
    case 409:
      throw new ApiConflictError(meta, body);
    case 422:
      throw new ApiValidationError(
        meta,
        body as { detail: Array<{ loc: Array<string | number>; msg: string; type: string }> },
      );
    case 429:
      throw new ApiRateLimitError(meta, body);
    default:
      if (resp.status >= 500) {
        throw new ApiServerError(meta, body);
      }
      throw new ApiError(`HTTP ${resp.status}`, 'unknown', meta, body);
  }
};

/* ============================================================================
 * Core request
 * ============================================================================ */

export async function request<TSchema extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<TSchema> & { schema: TSchema },
): Promise<ApiResult<z.infer<TSchema>>>;
export async function request(path: string, options?: RequestOptions): Promise<ApiResult<unknown>>;
export async function request<TSchema extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<TSchema> = {},
): Promise<ApiResult<unknown>> {
  const method: HttpMethod = options.method ?? 'GET';
  const url = `${joinUrl(config.baseUrl, path)}${buildSearch(options.query)}`;
  const retry: RetryConfig = { ...config.retry, ...options.retry };
  const timeoutMs = options.timeoutMs ?? config.timeoutMs;
  const request_id = newIdempotencyKey();

  // Build headers (idempotency key, request id, bearer, JSON content-type).
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': request_id,
    ...authHeader(),
    ...options.headers,
  };
  if (requiresIdempotencyKey(method)) {
    headers['Idempotency-Key'] = options.idempotencyKey ?? request_id;
  }
  let body: BodyInit | undefined;
  if (options.formData !== undefined) {
    body = options.formData;
  } else if (options.body !== undefined && options.body !== null) {
    body = JSON.stringify(options.body);
    headers['Content-Type'] = 'application/json';
  }

  let lastError: unknown = null;
  let attempts = 0;
  for (let i = 0; i < retry.attempts; i++) {
    attempts = i + 1;
    // Per-attempt timeout signal combined with the caller's signal.
    const ctrl = new AbortController();
    const timeoutHandle = setTimeout(() => {
      ctrl.abort(new DOMException('Timeout', 'TimeoutError'));
    }, timeoutMs);
    const onCallerAbort = (): void => ctrl.abort();
    options.signal?.addEventListener('abort', onCallerAbort, { once: true });

    let resp: Response;
    try {
      const init: RequestInit = {
        method,
        headers,
        signal: ctrl.signal,
        credentials: 'omit',
        cache: 'no-store',
      };
      if (body !== undefined) {
        init.body = body;
      }
      resp = await fetch(url, init);
    } catch (err) {
      clearTimeout(timeoutHandle);
      options.signal?.removeEventListener('abort', onCallerAbort);
      // Aborted by caller — propagate immediately, do NOT retry.
      if (options.signal?.aborted === true) {
        throw new ApiAbortedError({
          url,
          method,
          status: 0,
          request_id,
          attempts,
          mock: false,
        });
      }
      // Timeout vs network — both are retryable.
      const isTimeout =
        err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError');
      const meta: ApiErrorMeta = { url, method, status: 0, request_id, attempts, mock: false };
      lastError = isTimeout ? new ApiTimeoutError(meta) : new ApiNetworkError(meta, err);
      if (i < retry.attempts - 1) {
        await wait(fullJitter(i, retry.baseMs, retry.capMs), options.signal);
        continue;
      }
      throw lastError;
    }
    clearTimeout(timeoutHandle);
    options.signal?.removeEventListener('abort', onCallerAbort);

    // Read body.
    const parsed = await parseResponse(resp);
    const meta: ApiErrorMeta = {
      url,
      method,
      status: resp.status,
      request_id,
      attempts,
      mock: parsed.mock,
    };

    if (resp.ok) {
      // Optional schema parse at the boundary.
      let data: unknown = parsed.body;
      if (options.schema !== undefined) {
        const parseResult = options.schema.safeParse(parsed.body);
        if (!parseResult.success) {
          throw new ApiError(
            `Response shape did not match schema: ${parseResult.error.message}`,
            'unknown',
            meta,
            parsed.body,
          );
        }
        data = parseResult.data;
      }
      // Capture exposed response headers (CORS-permitted set) — lower-cased
      // for predictable lookup. Callers use this for things like pagination
      // totals via `x-total-count`.
      const respHeaders: Record<string, string> = {};
      resp.headers.forEach((v, k) => {
        respHeaders[k.toLowerCase()] = v;
      });
      return { data, meta: { ...meta, headers: respHeaders } };
    }

    // 401 → fire onAuthError so the session store clears and we redirect.
    //
    // EXCEPT for CLIENT-plane endpoints (/users/me/*): a 401 there while signed
    // in as an OPERATOR is EXPECTED (an operator key isn't a client key) — it
    // means "not a client", NOT "session expired". Logging the operator out for
    // it is the mission-control / equity-curve logout bug (those panels hydrate
    // from /users/me/pnl). A genuine session expiry still trips onAuthError via
    // the operator-plane endpoints, so the kill-switch is intact. See the
    // [[auth-two-surfaces]] split + admin-client-reports.ts (the operator twins).
    if (resp.status === 401) {
      const isClientPlane = path.includes('/users/me');
      if (!isClientPlane) {
        try {
          config.onAuthError();
        } catch {
          /* ignored */
        }
      }
      throw new ApiAuthError(meta, parsed.body);
    }

    // For retryable statuses (5xx, 429), retry; otherwise throw immediately.
    try {
      throwForStatus(resp, parsed.body, meta);
    } catch (err) {
      if (isRetryableError(err) && i < retry.attempts - 1) {
        lastError = err;
        await wait(fullJitter(i, retry.baseMs, retry.capMs), options.signal);
        continue;
      }
      throw err;
    }
  }
  // Unreachable — the loop either returns ok or throws. Defend anyway.
  throw lastError ?? new Error('request: exhausted retries with no error captured');
}

/* ============================================================================
 * Convenience helpers — most resource files prefer these.
 * ============================================================================ */

export const get = <TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options?: Omit<RequestOptions<TSchema>, 'method' | 'schema' | 'body' | 'formData'>,
): Promise<ApiResult<z.infer<TSchema>>> =>
  request(path, { ...options, method: 'GET', schema });

export const post = <TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  body: unknown,
  options?: Omit<RequestOptions<TSchema>, 'method' | 'schema' | 'body'>,
): Promise<ApiResult<z.infer<TSchema>>> =>
  request(path, { ...options, method: 'POST', body, schema });

export const patch = <TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  body: unknown,
  options?: Omit<RequestOptions<TSchema>, 'method' | 'schema' | 'body'>,
): Promise<ApiResult<z.infer<TSchema>>> =>
  request(path, { ...options, method: 'PATCH', body, schema });

export const put = <TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  body: unknown,
  options?: Omit<RequestOptions<TSchema>, 'method' | 'schema' | 'body'>,
): Promise<ApiResult<z.infer<TSchema>>> =>
  request(path, { ...options, method: 'PUT', body, schema });

export const del = (
  path: string,
  options?: Omit<RequestOptions, 'method' | 'body' | 'formData'>,
): Promise<ApiResult<unknown>> => request(path, { ...options, method: 'DELETE' });
